import React, { useState, useEffect, useMemo } from "react";
import { Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { MobileModal } from "./MobileModal";
import styles from "./MobileDatePicker.module.css";

interface MobileDatePickerProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  value?: string | Dayjs; // Accepts 'YYYY-MM-DD' or dayjs object
  onChange: (date: string) => void;
  footerText?: string;
}

const WEEKS = ["日", "一", "二", "三", "四", "五", "六"];

const cx = (...classNames: Array<string | false | null>) => classNames.filter(Boolean).join(" ");

export const MobileDatePicker: React.FC<MobileDatePickerProps> = ({ visible, onClose, title = "選擇日期", value, onChange, footerText = "請滑動或點擊選擇精準日期" }) => {
  // State for the currently viewed month/year
  const [viewDate, setViewDate] = useState<Dayjs>(dayjs());
  // State for the selected date
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);

  // Initialize state when opening or value changing
  useEffect(() => {
    if (visible) {
      if (value) {
        const d = dayjs(value);
        if (d.isValid()) {
          setSelectedDate(d);
          setViewDate(d);
        } else {
          setSelectedDate(null);
          setViewDate(dayjs());
        }
      } else {
        setSelectedDate(null);
        setViewDate(dayjs());
      }
    }
  }, [visible, value]);

  const handleYearChange = (year: number) => {
    setViewDate(viewDate.year(year));
  };

  const handleMonthChange = (month: number) => {
    setViewDate(viewDate.month(month - 1)); // Antd Select 1-12, dayjs 0-11
  };

  const handleDateClick = (date: Dayjs) => {
    setSelectedDate(date);
    onChange(date.format("YYYY-MM-DD"));
    console.log(date.format("YYYY-MM-DD"));
    onClose();
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const startOfMonth = viewDate.startOf("month");
    const startDayOfWeek = startOfMonth.day(); // 0 (Sunday) to 6 (Saturday)
    const daysInMonth = viewDate.daysInMonth();

    const days = [];

    // Empty slots for previous month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ type: "empty", key: `empty-${i}` });
    }

    // Days of current month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = viewDate.date(i);
      days.push({ type: "day", date, key: date.format("YYYY-MM-DD") });
    }

    return days;
  }, [viewDate]);

  // Year options (e.g., 1900 - Current)
  const yearOptions = useMemo(() => {
    const currentYear = dayjs().year();
    const years = [];
    for (let y = currentYear - 100; y <= currentYear; y++) {
      years.push({ label: `${y}年`, value: y });
    }
    return years; // Ascending order
  }, []);

  // Month options
  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      label: `${i + 1}月`,
      value: i + 1,
    }));
  }, []);

  return (
    <MobileModal visible={visible} onClose={onClose} title={title} footerText={footerText}>
      <div className={styles.container}>
        {/* Year/Month Selectors */}
        <div className={styles.selectors}>
          <div className={styles.selectField}>
            <Select
              size="large"
              value={viewDate.year()}
              onChange={handleYearChange}
              options={yearOptions}
              popupMatchSelectWidth={false}
              className={styles.selectRoot}
              classNames={{ popup: { root: styles.selectPopup } }}
            />
          </div>
          <div className={styles.selectField}>
            <Select
              size="large"
              value={viewDate.month() + 1}
              onChange={handleMonthChange}
              options={monthOptions}
              popupMatchSelectWidth={false}
              className={styles.selectRoot}
              classNames={{ popup: { root: styles.selectPopup } }}
            />
          </div>
        </div>

        {/* Calendar Grid */}
        <div className={styles.calendar}>
          {/* Week Headers */}
          <div className={styles.weekHeader}>
            {WEEKS.map((w) => (
              <div key={w} className={styles.weekCell}>
                {w}
              </div>
            ))}
          </div>

          {/* Days */}
          <div className={styles.daysGrid}>
            {calendarDays.map((item: any) => {
              if (item.type === "empty") {
                return <div key={item.key} />;
              }

              const isSelected = selectedDate && item.date.isSame(selectedDate, "day");
              const isToday = item.date.isSame(dayjs(), "day");

              return (
                <div
                  key={item.key}
                  onClick={() => handleDateClick(item.date)}
                  className={styles.dayCell}
                >
                  <div className={cx(styles.dayButton, isSelected && styles.selectedDay, isToday && !isSelected && styles.todayDay)}>
                    {item.date.date()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </MobileModal>
  );
};
